import { Link } from 'react-router-dom';
import { useConsultation } from '@/components/ConsultationModal';
import FadeIn from '@/components/FadeIn';
import { IMAGES } from '@/lib/constants';
import {
  BookOpen, BarChart3, Shield, Users, ArrowRight, Check,
  ClipboardCheck, TrendingUp, Calculator, Handshake, Clock, FileText
} from 'lucide-react';

const trustItems = [
  { icon: BookOpen, text: 'Professional Bookkeeping Support' },
  { icon: BarChart3, text: 'Clear Monthly Reporting' },
  { icon: ClipboardCheck, text: 'Organized, Tax-Ready Books' },
  { icon: Handshake, text: 'Coordinated CPA Support' },
  { icon: Clock, text: 'Responsive Service' },
];

const coreServices = [
  {
    icon: BookOpen,
    title: 'Premier Bookkeeping',
    description: 'Accurate, organized monthly bookkeeping that keeps your business running smoothly. We handle the numbers so you can focus on growing your business.',
    featured: true,
  },
  {
    icon: BarChart3,
    title: 'Monthly Financial Reporting',
    description: 'Clear profit and loss statements, balance sheets, and cash flow reports delivered monthly so you always know where your business stands.',
  },
  {
    icon: Calculator,
    title: 'Accounting Support & Consulting',
    description: 'Strategic financial guidance and business consulting to help you make informed decisions, reduce overhead, and strengthen your operations.',
  },
];

const reasons = [
  { title: 'Executive-Level Professionalism', text: 'Every client receives the same structured, polished, and responsive service you would expect from a high-end accounting firm.' },
  { title: 'Personal, Hands-On Support', text: 'You work directly with William Covington. No call centers, no runaround, no being passed between departments.' },
  { title: 'Built for Small Business', text: 'We understand the challenges of running a growing business. Our services are designed specifically for owner-operators and entrepreneurs.' },
  { title: 'CPA-Coordinated Service', text: 'When higher-level accounting or tax support is needed, we coordinate directly with trusted CPAs on your behalf.' },
];

const outcomes = [
  { title: 'Stay Organized', text: 'Never wonder where your business stands financially. Clean, categorized books give you confidence month after month.' },
  { title: 'Understand Your Numbers', text: 'Clear monthly reports translate your financial data into actionable business intelligence you can use.' },
  { title: 'Tax-Ready Year-Round', text: 'Eliminate the stress of tax season. Your books stay current, organized, and ready for your CPA at all times.' },
  { title: 'Better Business Decisions', text: 'When you can see your numbers clearly, you make smarter decisions about spending, hiring, and growth.' },
  { title: 'Avoid Financial Confusion', text: 'Stop guessing about cash flow, profitability, or outstanding obligations. We bring clarity to your finances.' },
  { title: 'Build Stability', text: 'A well-organized financial foundation supports every other part of your business and positions you for growth.' },
];

const cpaPoints = [
  'Books maintained clean and organized year-round',
  'Direct coordination with your CPA or one we recommend',
  'Tax-ready financials delivered ahead of deadlines',
  'No scrambling at tax season or audit time',
  'Clear communication between all financial parties',
];

const testimonials = [
  { quote: 'William brought complete order to our books after years of disorganization. The monthly reports give us clarity we never had before.', name: 'Charles Wimley', role: 'Owner, M&M Restaurant' },
  { quote: 'Having someone I trust handle our bookkeeping and coordinate with our CPA has been invaluable. I finally understand our numbers.', name: 'Ana Bridewell', role: 'Founder, Connection Strategist' },
  { quote: 'The professionalism and responsiveness are unmatched. Our books are always current, organized, and ready for anything.', name: 'Mozy Tehrani', role: 'CEO, Wine Consigners' },
];

export default function HomePage() {
  const { setOpen } = useConsultation();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Executive office" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 hero-gradient" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-6">
              William Covington & Associates
            </p>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-white max-w-4xl leading-[1.1]">
              Premier Bookkeeping for Small Businesses and Entrepreneurs
            </h1>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="font-body text-base lg:text-lg text-white/65 mt-6 max-w-2xl leading-relaxed">
              Clean books, clear monthly reporting, and reliable financial organization with coordinated CPA support when needed.
            </p>
          </FadeIn>
          <FadeIn delay={300}>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button
                onClick={() => setOpen(true)}
                className="bg-wc-gold text-white px-8 py-4 font-body text-sm uppercase tracking-[0.15em] hover:bg-wc-gold-hover transition-colors duration-300"
                data-testid="hero-cta-primary"
              >
                Schedule a Consultation
              </button>
              <Link
                to="/contact"
                className="border border-white/30 text-white px-8 py-4 font-body text-sm uppercase tracking-[0.15em] hover:bg-white/10 transition-colors duration-300 text-center"
                data-testid="hero-cta-secondary"
              >
                Request Bookkeeping Support
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROMOTIONAL BANNER */}
      <section className="bg-wc-black py-10 lg:py-12 border-b border-wc-gold/20" data-testid="promo-banner">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
            <FadeIn>
              <div className="flex items-start gap-5">
                <div className="w-px h-16 bg-wc-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-2">Limited Offer</p>
                  <h3 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white leading-tight">
                    Book Cleanup — <span className="text-wc-gold">$200</span>
                  </h3>
                  <p className="font-body text-sm text-white/55 mt-2 leading-relaxed">
                    Get your books organized, current, and tax-ready. Includes up to 5 hours of labor. Additional work is $60 per hour depending on complexity of books.
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:justify-end">
                <button
                  onClick={() => setOpen(true)}
                  className="bg-wc-gold text-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-wc-gold-hover transition-colors duration-300 whitespace-nowrap"
                  data-testid="promo-cta"
                >
                  Schedule a Free 15-Min Consultation
                </button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-wc-border py-8 bg-white" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
            {trustItems.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <item.icon className="w-4 h-4 text-wc-gold flex-shrink-0" strokeWidth={1.5} />
                <span className="font-body text-xs sm:text-sm text-wc-muted">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="py-24 lg:py-32 bg-white" data-testid="core-services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Our Services</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black mb-16">
              Financial Clarity,<br />Delivered Monthly
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-px bg-wc-border">
            {coreServices.map((service, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className={`bg-white p-10 lg:p-12 h-full ${service.featured ? 'border-t-2 border-t-wc-gold' : ''}`}>
                  <service.icon className="w-7 h-7 text-wc-gold mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-2xl font-normal text-wc-black mb-4">{service.title}</h3>
                  <p className="font-body text-sm text-wc-muted leading-relaxed mb-6">{service.description}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.1em] text-wc-black border-b border-wc-black pb-0.5 hover:border-wc-gold hover:text-wc-gold transition-colors duration-300"
                    data-testid={`service-link-${i}`}
                  >
                    Learn More <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 lg:py-32 bg-wc-bg-alt" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div className="overflow-hidden">
                <img src={IMAGES.williamGlasses} alt="William K. Covington" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div>
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Why Choose Us</p>
                <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black mb-10">
                  Structure. Clarity.<br />Confidence.
                </h2>
                <div className="space-y-8">
                  {reasons.map((reason, i) => (
                    <div key={i} className="flex gap-5">
                      <div className="w-px bg-wc-gold flex-shrink-0 mt-1" style={{ minHeight: '40px' }} />
                      <div>
                        <h4 className="font-body text-base font-semibold text-wc-black mb-1">{reason.title}</h4>
                        <p className="font-body text-sm text-wc-muted leading-relaxed">{reason.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* BOOKKEEPING OUTCOMES */}
      <section className="py-24 lg:py-32 bg-white" data-testid="outcomes-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">The Outcome</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black">
                What Clean Books Mean<br />for Your Business
              </h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-wc-border">
            {outcomes.map((outcome, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="bg-white p-10 h-full">
                  <span className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="font-heading text-xl font-normal text-wc-black mt-4 mb-3">{outcome.title}</h3>
                  <p className="font-body text-sm text-wc-muted leading-relaxed">{outcome.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CPA COORDINATION */}
      <section className="py-24 lg:py-32 bg-wc-black" data-testid="cpa-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div>
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">CPA Coordination</p>
                <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-white mb-8">
                  Seamless Tax Support<br />When You Need It
                </h2>
                <p className="font-body text-base lg:text-lg text-white/55 leading-relaxed mb-6">
                  We keep your books clean, organized, and tax-ready year-round. When higher-level accounting or tax support is needed, we coordinate directly with a trusted CPA on your behalf.
                </p>
                <p className="font-body text-sm text-white/45 leading-relaxed">
                  No scrambling at tax season. No confusion between your bookkeeper and your accountant. Just structured, coordinated financial support that keeps your business running smoothly.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div className="border border-white/10 p-10 lg:p-12">
                <div className="space-y-6">
                  {cpaPoints.map((point, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <Check className="w-4 h-4 text-wc-gold flex-shrink-0 mt-1" strokeWidth={2} />
                      <span className="font-body text-sm text-white/75 leading-relaxed">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ASSOCIATE NETWORK PREVIEW */}
      <section className="py-24 lg:py-32 bg-wc-bg-alt" data-testid="network-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div>
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Beyond the Books</p>
                <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black mb-8">
                  Extended Support Through<br />Trusted Associates
                </h2>
                <p className="font-body text-base lg:text-lg text-wc-muted leading-relaxed mb-6">
                  In addition to premier bookkeeping and accounting support, clients of William Covington & Associates may access broader business support through our trusted associate network.
                </p>
                <Link
                  to="/associate-network"
                  className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.1em] text-wc-black border-b border-wc-black pb-0.5 hover:border-wc-gold hover:text-wc-gold transition-colors duration-300"
                  data-testid="network-preview-link"
                >
                  Explore Our Network <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div className="overflow-hidden">
                <img
                  src={IMAGES.network}
                  alt="Business partnership"
                  className="w-full aspect-[4/3] object-cover hover:scale-[1.02] transition-transform duration-700"
                  loading="lazy"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Client Testimonials</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black mb-16">
              Trusted by Business Owners
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-px bg-wc-border">
            {testimonials.map((t, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="bg-white p-10 h-full flex flex-col">
                  <div className="w-8 h-px bg-wc-gold mb-6" />
                  <p className="font-body text-base text-wc-black leading-relaxed mb-8 flex-1 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div>
                    <p className="font-body text-sm font-semibold text-wc-black">{t.name}</p>
                    <p className="font-body text-xs text-wc-muted mt-0.5">{t.role}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 lg:py-32" data-testid="final-cta-section">
        <div className="absolute inset-0">
          <img src={IMAGES.darkTexture} alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-wc-black/85" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-6">Get Started</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-white mb-6">
              Build Financial Clarity<br />for Your Business
            </h2>
            <p className="font-body text-base lg:text-lg text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed">
              Schedule a consultation today and discover how professional bookkeeping support can bring order, clarity, and confidence to your business operations.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="bg-wc-gold text-white px-10 py-4 font-body text-sm uppercase tracking-[0.15em] hover:bg-wc-gold-hover transition-colors duration-300"
              data-testid="final-cta-button"
            >
              Schedule a Consultation
            </button>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
